import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CreateTenantBlade } from './CreateTenantBlade';
import { TenantDetailBlade } from './TenantDetailBlade';
import { TenantsList } from './TenantsList';

/**
 * The Tenants section route. Renders the resource-list page and hosts the two
 * blades it drives: the create blade (command-bar action) and the detail blade
 * (row click). A successful provision bumps `refreshToken` to reload the list.
 *
 * This is the seam Dev-C's routes.tsx mounts at `/tenants`.
 */
export function TenantsRoute(): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const refresh = useCallback(() => setRefreshToken((n) => n + 1), []);

  return (
    <>
      <TenantsList
        refreshToken={refreshToken}
        onCreate={() => setCreateOpen(true)}
        onSelect={(id) => setSelectedId(id)}
      />
      <CreateTenantBlade
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />
      <TenantDetailBlade
        open={selectedId !== null}
        tenantId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}

/**
 * Deep-link detail route for `/tenants/:id`. Reads the id from the router and
 * renders the detail blade open; closing navigates back to the list.
 *
 * This is the seam Dev-C's routes.tsx mounts at `/tenants/:id`.
 */
export function TenantDetailRoute(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  return (
    <TenantDetailBlade
      open={true}
      tenantId={id ?? null}
      onClose={() => {
        void navigate('/tenants');
      }}
    />
  );
}

export { TenantsList } from './TenantsList';
export { CreateTenantBlade } from './CreateTenantBlade';
export { TenantDetailBlade } from './TenantDetailBlade';
